import React from "react";
import { withRouter } from "react-router";
import { Container } from "../../components/container";
import ServiceFactory from "../../service";

/*
 * Ship connectors OAuth callback (Content Hub Builder step 4, 2026-09-27) — /OAuth/:provider for
 * pinterest | linkedin | youtube | tiktok. Same shape as the Meta callback:
 *   1. POST the code to /v1/social/oauth/{provider}/complete → the server exchanges it, stages the token
 *      bundle server-side and returns what the grant can publish to (boards / organizations / channel / user).
 *   2. The admin picks one.
 *   3. POST the pick to /v1/social/oauth/{provider}/finish → the server stores the bundle and the
 *      social_account row. No token reaches the browser.
 * redirect_uri is `${origin}/OAuth/${provider}` — whitelist it in each provider's developer app.
 */

const PRETTY = {
  pinterest: "Pinterest",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  tiktok: "TikTok"
};
const KIND = {
  board: "Board",
  organization: "Company page",
  channel: "Channel",
  user: "Account"
};
function svc(api) {
  const S = ServiceFactory.getType(api, "product");
  return new S();
}
class SocialOAuthCallback extends React.Component {
  constructor(props) {
    super(props);
    this.provider = String(props.params && props.params.provider || "").toLowerCase();
    this.state = {
      phase: "exchanging",
      error: null,
      accounts: null,
      chosen: null,
      busy: false,
      connected: null
    };
  }
  componentDidMount() {
    const q = new URLSearchParams(window.location.search);
    const err = q.get("error_description") || q.get("error");
    const code = q.get("code");
    if (!PRETTY[this.provider]) {
      this.setState({
        phase: "error",
        error: "Unknown provider."
      });
      return;
    }
    if (err) {
      this.setState({
        phase: "error",
        error: /denied|access_denied/i.test(err) ? "Access was not granted. To connect, approve the requested permissions." : String(err)
      });
      return;
    }
    if (!code) {
      this.setState({
        phase: "error",
        error: `No authorization code returned from ${PRETTY[this.provider]}.`
      });
      return;
    }
    this.complete(code);
  }
  redirectUri() {
    return window.location.origin + "/OAuth/" + this.provider;
  }
  async complete(code) {
    try {
      const r = await svc(`/social/oauth/${this.provider}/complete`).postAsync({
        code,
        redirectUri: this.redirectUri()
      });
      const accounts = r && r.accounts || [];
      if (!accounts.length) {
        this.setState({
          phase: "error",
          error: `${PRETTY[this.provider]} returned nothing to connect — ${this.provider === "linkedin" ? "you need to be an administrator of a company page." : this.provider === "pinterest" ? "create a board first." : "check the account has a channel."}`
        });
        return;
      }
      this.setState({
        phase: "pick",
        accounts,
        chosen: accounts[0].externalId
      });
    } catch (e) {
      this.setState({
        phase: "error",
        error: e && e.message && e.message.length < 240 ? e.message : `Could not complete the connection with ${PRETTY[this.provider]}.`
      });
    }
  }
  async finish() {
    const a = (this.state.accounts || []).find(x => x.externalId === this.state.chosen);
    this.setState({
      busy: true,
      error: null
    });
    try {
      const r = await svc(`/social/oauth/${this.provider}/finish`).postAsync({
        externalId: a.externalId,
        handle: a.handle,
        displayName: a.displayName
      });
      this.setState({
        busy: false,
        phase: "done",
        connected: r && r.connected || []
      });
    } catch (e) {
      this.setState({
        busy: false,
        error: e && e.message && e.message.length < 200 ? e.message : "Could not save the connection."
      });
    }
  }
  render() {
    const {
      phase,
      error,
      accounts,
      chosen,
      busy
    } = this.state;
    const name = PRETTY[this.provider] || "provider";
    return /*#__PURE__*/React.createElement(Container, {
      title: `Connect ${name}`,
      css: "margin-squeeze"
    }, phase === "exchanging" && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Finishing the connection with ", name, "\u2026"), phase === "error" && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#b3261e",
        maxWidth: "70ch"
      }
    }, error), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Back to Connections")), phase === "pick" && /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: 520,
        textAlign: "left"
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 12px",
        textAlign: "left"
      }
    }, "Choose where Plunk publishes."), /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px solid var(--plk-border, #e4e7ec)",
        borderRadius: 8,
        overflow: "hidden"
      }
    }, accounts.map((a, i) => {
      const sel = chosen === a.externalId;
      return /*#__PURE__*/React.createElement("div", {
        key: a.externalId,
        role: "button",
        onClick: () => this.setState({
          chosen: a.externalId
        }),
        style: {
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          textAlign: "left",
          padding: "12px 14px",
          cursor: "pointer",
          borderTop: i === 0 ? "none" : "1px solid var(--plk-border, #eef0f3)",
          background: sel ? "var(--plk-surface-alt, #f3f7ff)" : "transparent"
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "radio",
        name: "acct",
        checked: sel,
        readOnly: true,
        style: {
          margin: "3px 0 0",
          flex: "0 0 auto",
          width: 16,
          height: 16
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: "1 1 auto",
          minWidth: 0,
          lineHeight: 1.4
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontWeight: 600
        }
      }, a.displayName || a.handle), /*#__PURE__*/React.createElement("div", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, KIND[a.kind] || a.kind, a.handle && a.handle !== a.displayName ? ` · ${a.handle}` : "")));
    })), error && /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#b3261e"
      }
    }, error), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 14,
        display: "flex",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || !chosen,
      onClick: () => this.finish()
    }, busy ? "Connecting…" : "Connect"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Cancel"))), phase === "done" && /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: "60ch"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#1d7a3e"
      }
    }, "Connected ", name, ". It now appears as a target under Content Hub \u203A Ship and on every piece's Publish menu."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => this.props.history.push("/Content")
    }, "Go to Content Hub"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Connections"))));
  }
}
const Routable = withRouter(SocialOAuthCallback);
export class SocialOAuthCallbackViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, this.props);
  }
}
export default {
  SocialOAuthCallbackViewComponent
};